// ./src/pages/private/EventDiscountCodesPage/DiscountTicketTypesField.tsx
//
// "Which ticket types is this code for?" (ROADMAP A2h #163, owner 2026-10-15: "discount code should be able to be applied
// to specific ticket types") — pick several of the event's ticket types, admissions or add-ons. Shown when the code's
// "Applies to" is "Specific ticket types"; checkout then reduces only those lines, and refuses the code on an order that
// holds none of them, naming them.
//
// ⚠ The offer is exactly what the SERVER accepts (`DiscountCodeCore.checkTicketTypes`): this event's types that are not
//   removed — a hidden or sold-out type may still be picked (a code can be ready before the type opens).
// ⚠ Every id in the value gets a name or a placeholder — one the list cannot name (a failed read) can still be taken off.

import React, { useEffect, useMemo, useState } from 'react';
import { Input, Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { TicketType, TicketTypeService } from '@/utils/api';
import { CheckableMultiSelect, ErrorAlert } from '@/components/generic';

export interface DiscountTicketTypesFieldProps {
  eventId: string;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  error?: React.ReactNode;
}

export const DiscountTicketTypesField: React.FC<DiscountTicketTypesFieldProps> = ({ eventId, value, onChange, disabled = false, error }) => {
  const { t } = useTranslation(['events', 'common']);
  const [types, setTypes] = useState<TicketType[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [readError, setReadError] = useState<OpResult | null>(null);

  useEffect(() => {
    let alive = true;

    setLoaded(false);
    TicketTypeService.listAll({ eventId: [eventId] }).then((res) => {
      if (!alive) {
        return;
      }

      setReadError(res.didSucceed() ? null : res);
      setTypes(res.didSucceed() && Array.isArray(res.data) ? (res.data as TicketType[]) : []);
      setLoaded(true);
    });

    return () => {
      alive = false;
    };
  }, [eventId]);

  const options = useMemo(() => {
    const known = types
      .filter((tt) => !!tt.id)
      .map((tt) => ({
        value: tt.id as string,
        label: tt.isAddOn
          ? t('events:discounts.typeAddOn', { defaultValue: '{{name}} (add-on)', name: tt.name || '' })
          : tt.name || t('events:discounts.typeUnnamed', { defaultValue: 'Unnamed ticket type' }),
      }));
    const knownIds = new Set(known.map((o) => o.value));
    // A picked id the read could not name still gets a row, so it can be taken off.
    const unknown = value
      .filter((id) => !knownIds.has(id))
      .map((id) => ({ value: id, label: t('events:discounts.typeUnknown', { defaultValue: 'A ticket type that could not be loaded' }) }));

    return [...known, ...unknown];
  }, [types, value, t]);

  return (
    <Stack gap="xs">
      <CheckableMultiSelect
        label={t('events:discounts.ticketTypes', { defaultValue: 'Ticket types' })}
        placeholder={
          // ⚠ Only a read that WORKED and found nothing says so.
          loaded && !readError && types.length === 0
            ? t('events:discounts.ticketTypesNone', { defaultValue: 'This event has no ticket types yet' })
            : t('events:discounts.ticketTypesPh', { defaultValue: 'Pick the ticket types this code is for' })
        }
        data={options}
        value={value}
        onChange={onChange}
        disabled={disabled || !loaded || options.length === 0}
        searchable
      />
      <Text size="md" c="dimmed">
        {t('events:discounts.ticketTypesHint', {
          defaultValue: 'The code takes its discount off these tickets only. A buyer whose order has none of them is told which tickets it is for.',
        })}
      </Text>
      {readError ? (
        <ErrorAlert
          error={readError}
          title={t('events:discounts.ticketTypesLoadFailed', { defaultValue: 'Could not load the ticket types' })}
          page="Discount codes"
          operation="ticketTypeList"
        />
      ) : null}
      {error ? <Input.Error size="md">{error}</Input.Error> : null}
    </Stack>
  );
};
